import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Collapse,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'

type McpServer = {
  id?: string
  name: string
  transport: 'stdio' | 'sse'
  command?: string
  args?: string[]
  url?: string
  enabled?: boolean
  description?: string
}

type McpConfig = {
  mcp_enabled: boolean
  mcp_use_builtin_kb: boolean
  mcp_display_label: string
  mcp_max_tool_rounds: number
  servers: McpServer[]
  integration_status?: string
  runtime_ready?: boolean
  help?: { what_is_mcp?: string; current_capability?: string }
}

export default function McpSettings() {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [validating, setValidating] = useState(false)
  const [checkResult, setCheckResult] = useState<{
    ok: boolean
    message: string
    issues?: string[]
    warnings?: string[]
  } | null>(null)

  const mcpEnabled = Form.useWatch('mcp_enabled', form)

  const load = () =>
    adminApi.get<McpConfig>('/admin/settings/mcp').then((r) => {
      const d = r.data
      form.setFieldsValue({
        mcp_enabled: d.mcp_enabled ?? false,
        mcp_use_builtin_kb: d.mcp_use_builtin_kb ?? true,
        mcp_display_label: d.mcp_display_label ?? '调用知识库工具集',
        mcp_max_tool_rounds: d.mcp_max_tool_rounds ?? 5,
        servers: (d.servers || []).map((s) => ({
          ...s,
          args: Array.isArray(s.args) ? s.args.join(', ') : s.args || '',
        })),
      })
    })

  useEffect(() => {
    load().catch((e) => message.error(e.message))
  }, [form])

  const save = () => {
    void form.validateFields().then((v) => {
      setLoading(true)
      const servers = (v.servers || []).map((s: McpServer & { args?: string }) => ({
        ...s,
        args:
          typeof s.args === 'string'
            ? s.args
                .split(',')
                .map((a) => a.trim())
                .filter(Boolean)
            : s.args || [],
      }))
      adminApi
        .put('/admin/settings/mcp', { ...v, servers })
        .then(() => {
          message.success('MCP 配置已保存')
          setCheckResult(null)
          return load()
        })
        .catch((e) => message.error(e.message))
        .finally(() => setLoading(false))
    })
  }

  const validateCfg = () => {
    setValidating(true)
    adminApi
      .post<{ ok: boolean; message: string; issues?: string[]; warnings?: string[] }>(
        '/admin/settings/mcp/validate',
      )
      .then((r) => setCheckResult(r.data))
      .catch((e) => message.error(e.message))
      .finally(() => setValidating(false))
  }

  return (
    <div>
      <PageHeader
        title="MCP 工具配置"
        description="为对话接入外部工具能力（可先做展示与预留，再逐步接通运行时）"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="MCP 是什么？"
        description={
          <div>
            <p style={{ margin: '0 0 8px' }}>
              <strong>MCP（Model Context Protocol，模型上下文协议）</strong>
              是一套开放标准，让 AI 在回答时能按统一方式调用外部「工具」——例如查业务数据库、调 OA
              接口、读代码仓库，而不仅是在本系统知识库里检索文档。
            </p>
            <p style={{ margin: 0 }}>
              您在其他产品截图里看到的「调用 MCP 工具集」，指的就是这一类<strong>工具调用步骤</strong>。
              本页用于提前配置：是否在对话中展示该步骤、展示名称，以及将来要连接的 MCP 服务地址。
            </p>
          </div>
        }
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="当前版本说明"
        description="保存配置后，若开启「将知识库检索展示为 MCP 工具步骤」，前台对话时间线会使用您填写的展示名称；与外部 MCP Server 的实时协议调用将在后续版本接入。未开启时仍仅使用内置自纠错多轮知识库检索。"
      />

      <Collapse
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'faq',
            label: '常见疑问',
            children: (
              <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
                <li>
                  <strong>和「问答设置」里的自纠错模式有什么区别？</strong>
                  自纠错模式是本系统内置的多轮知识库检索与答案核对；MCP 面向更广义的外部工具，二者可并存。
                </li>
                <li>
                  <strong>stdio / SSE 是什么？</strong>
                  stdio：本机启动一个 MCP 进程并通过标准输入输出通信；SSE：通过 HTTP 地址连接远程 MCP 服务。
                </li>
                <li>
                  <strong>现在配了 MCP 地址就能用吗？</strong>
                  地址会先保存在后台；真实调用需等运行时模块发布。可先开启「内置知识库映射」统一对话展示风格。
                </li>
              </ul>
            ),
          },
        ]}
      />

      <Form form={form} layout="vertical" initialValues={{ servers: [] }}>
        <Card title="总开关与对话展示" style={{ marginBottom: 16 }}>
          <Form.Item
            name="mcp_enabled"
            label="启用 MCP 相关能力"
            valuePropName="checked"
            extra="关闭时：对话不显示 MCP 风格步骤名称，仅走默认检索流程。"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="mcp_use_builtin_kb"
            label="将知识库检索展示为 MCP 工具步骤"
            valuePropName="checked"
            extra="开启后：自纠错检索在对话里显示为下方「步骤展示名称」，便于与截图中的「工具集」风格一致。"
          >
            <Switch disabled={!mcpEnabled} />
          </Form.Item>
          <Form.Item
            name="mcp_display_label"
            label="步骤展示名称"
            rules={[{ required: true, message: '请填写展示名称' }]}
            extra='例如：「调用知识库工具集」或「调用 MCP 工具集」（仅影响前台文案）。'
          >
            <Input placeholder="调用知识库工具集" maxLength={80} disabled={!mcpEnabled} />
          </Form.Item>
          <Form.Item
            name="mcp_max_tool_rounds"
            label="工具调用展示轮次上限"
            extra="与自纠错检索轮次配合使用，用于限制时间线中「工具」类步骤数量（1–10）。"
          >
            <InputNumber min={1} max={10} style={{ width: '100%' }} disabled={!mcpEnabled} />
          </Form.Item>
        </Card>

        <Card
          title="外部 MCP 服务（预留）"
          extra={
            <Typography.Text type="secondary">可选；配置后供后续版本连接</Typography.Text>
          }
        >
          <Form.List name="servers">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Card
                    key={key}
                    size="small"
                    type="inner"
                    style={{ marginBottom: 12 }}
                    title={`服务 #${name + 1}`}
                    extra={
                      <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)}>
                        删除
                      </Button>
                    }
                  >
                    <Form.Item {...rest} name={[name, 'name']} label="服务名称" rules={[{ required: true }]}>
                      <Input placeholder="例如：内部工单 MCP" />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'transport']} label="连接方式" initialValue="stdio">
                      <Select
                        options={[
                          { value: 'stdio', label: 'stdio（本机进程）' },
                          { value: 'sse', label: 'SSE（HTTP 远程）' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>
                      {() => {
                        const t = form.getFieldValue(['servers', name, 'transport'])
                        if (t === 'sse') {
                          return (
                            <Form.Item {...rest} name={[name, 'url']} label="SSE 地址" rules={[{ required: true }]}>
                              <Input placeholder="https://your-mcp.example.com/sse" />
                            </Form.Item>
                          )
                        }
                        return (
                          <>
                            <Form.Item {...rest} name={[name, 'command']} label="启动命令" rules={[{ required: true }]}>
                              <Input placeholder="例如：npx" />
                            </Form.Item>
                            <Form.Item
                              {...rest}
                              name={[name, 'args']}
                              label="命令参数"
                              extra="多个参数用英文逗号分隔"
                            >
                              <Input placeholder="例如：-y,@modelcontextprotocol/server-filesystem,/data" />
                            </Form.Item>
                          </>
                        )
                      }}
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'description']} label="说明">
                      <Input.TextArea rows={2} placeholder="该 MCP 提供哪些工具（便于管理员识别）" />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, 'enabled']} label="启用" valuePropName="checked" initialValue>
                      <Switch />
                    </Form.Item>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => add({ transport: 'stdio', enabled: true })} icon={<PlusOutlined />}>
                  添加 MCP 服务
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        {checkResult ? (
          <Alert
            style={{ marginBottom: 16 }}
            type={checkResult.ok ? 'success' : 'error'}
            message={checkResult.message}
            description={
              <>
                {(checkResult.issues || []).map((t) => (
                  <div key={t}>• {t}</div>
                ))}
                {(checkResult.warnings || []).map((t) => (
                  <div key={t} style={{ color: 'rgba(0,0,0,0.55)' }}>
                    提示：{t}
                  </div>
                ))}
              </>
            }
          />
        ) : null}

        <Space>
          <Button type="primary" onClick={save} loading={loading}>
            保存配置
          </Button>
          <Button onClick={validateCfg} loading={validating}>
            检查配置
          </Button>
        </Space>
      </Form>
    </div>
  )
}
